import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/category/variety", label: "综艺" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background-100/95 backdrop-blur-md border-b border-background-300/60"
          : "bg-gradient-to-b from-background-50/95 via-background-50/60 to-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-4 px-4 md:px-8 h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-lg bg-accent-500 flex items-center justify-center">
            <i className="ri-play-fill text-lg text-background-50" />
          </span>
          <span className="font-heading font-extrabold text-lg md:text-xl tracking-tight text-foreground-950">
            在线播放
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive
                    ? "text-accent-500 bg-accent-500/10"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop search */}
        <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、综艺"
              className="w-56 lg:w-64 pl-9 pr-3 py-2 text-sm rounded-md bg-background-100 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-950 bg-background-200/70 cursor-pointer"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {/* Mobile panel */}
      {menuOpen && (
        <div className="md:hidden bg-background-100/98 backdrop-blur-md border-t border-background-300/60 px-4 py-4 animate-fade-up">
          <form onSubmit={submitSearch} className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base font-medium cursor-pointer ${
                    isActive ? "text-accent-500 bg-accent-500/10" : "text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}